import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SessionFilters = ({ onFilterChange, sessionCount }) => {
  const [activeFilters, setActiveFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'name'
  });

  const statusFilters = [
    { value: 'all', label: 'All Sessions', icon: 'List', count: sessionCount },
    { value: 'active', label: 'Active', icon: 'Play', count: 8 },
    { value: 'rest', label: 'Rest Period', icon: 'Clock', count: 3 },
    { value: 'paused', label: 'Paused', icon: 'Pause', count: 1 },
    { value: 'completed', label: 'Completed', icon: 'CheckCircle', count: 2 }
  ];

  const sortOptions = [
    { value: 'name', label: 'Client Name', icon: 'User' },
    { value: 'duration', label: 'Session Duration', icon: 'Clock' },
    { value: 'progress', label: 'Progress', icon: 'TrendingUp' },
    { value: 'status', label: 'Status', icon: 'Activity' }
  ];

  const handleStatusFilter = (status) => {
    const newFilters = { ...activeFilters, status };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchChange = (e) => {
    const search = e?.target?.value;
    const newFilters = { ...activeFilters, search };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSortChange = (sortBy) => {
    const newFilters = { ...activeFilters, sortBy };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const newFilters = { status: 'all', search: '', sortBy: 'name' };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Session Filters</h2>
          <p className="text-sm text-muted-foreground">
            Refine and sort training sessions
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="breathing-focus"
        >
          <Icon name="X" size={16} className="mr-2" />
          Clear All
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Search by client or exercise..."
          value={activeFilters?.search}
          onChange={handleSearchChange}
          className="w-full"
        />
      </div>

      {/* Status Filters */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Filter by Status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {statusFilters?.map((filter) => (
            <Button
              key={filter?.value}
              variant={activeFilters?.status === filter?.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleStatusFilter(filter?.value)}
              className="flex justify-between w-full breathing-focus"
            >
              <div className="flex items-center space-x-2">
                <Icon name={filter?.icon} size={16} />
                <span>{filter?.label}</span>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full 
                ${activeFilters?.status === filter?.value
                  ? 'bg-primary/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground'}
                `}
              >
                {filter?.count}
              </span>
            </Button>
          ))}
        </div>
      </div>

      {/* Sort Options */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Sort by</h3>
        <div className="grid grid-cols-1 gap-2">
          {sortOptions?.map((option) => (
            <Button
              key={option?.value}
              variant={activeFilters?.sortBy === option?.value ? "secondary" : "ghost"}
              size="sm"
              onClick={() => handleSortChange(option?.value)}
              className="w-full justify-start breathing-focus"
            >
              <Icon name={option?.icon} size={16} className="mr-2" />
              {option?.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Active Filter Summary */}
      {(activeFilters?.status !== 'all' || activeFilters?.search || activeFilters?.sortBy !== 'name') && (
        <div className="mt-6 pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground mb-2">Active Filters</h4>
          <div className="flex flex-wrap gap-2">
            {activeFilters?.status !== 'all' && (
              <span className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Status: {statusFilters?.find(f => f?.value === activeFilters?.status)?.label}
                <button onClick={() => handleStatusFilter('all')}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {activeFilters?.search && (
              <span className="flex items-center gap-1 px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-full">
                Search: "{activeFilters?.search}"
                <button onClick={() => handleSearchChange({ target: { value: '' } })}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {activeFilters?.sortBy !== 'name' && (
              <span className="flex items-center gap-1 px-2 py-1 bg-wellness/10 text-wellness text-xs rounded-full">
                Sort: {sortOptions?.find(s => s?.value === activeFilters?.sortBy)?.label}
                <button onClick={() => handleSortChange('name')}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionFilters;
